import { Component, EventEmitter, Output, ViewChild, ElementRef, AfterViewInit, OnDestroy, ChangeDetectorRef, inject, Input } from '@angular/core';
import { LocationModel, Logger } from '../../../../../common';
import { signal } from '@angular/core'; // Angular Signals
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

interface Location {
  lat: number;
  lng: number;
}

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'] // Ensure the correct path to the SCSS file
})
export class MapComponent implements AfterViewInit, OnDestroy {
  @Output() emitLocation = new EventEmitter<LocationModel>(); // Emit LocationModel
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;

  // Signals for state management
  selectedLocation = signal<Location>({ lat: Number(LocationModel.createDefault().latitude), lng: Number(LocationModel.createDefault().latitude) }); // Default to Riyadh, Saudi Arabia
  zoom = signal(14); // Default zoom level
  latitude = signal(0);
  longitude = signal(0);

  @Input() enableQuickLatLongSearch: boolean = false; // Input for initial enableQuickLatLong
  latitudeInput: number | null = null;  // Input for latitude
  longitudeInput: number | null = null;  // Input for longitude

  map: any | undefined;
  marker: any | undefined;
  markerMoveTimeout: any;
  private cdr = inject(ChangeDetectorRef);
  geoCoder: any;

  ngAfterViewInit() {
    this.setFirstLocation();
    this.geoCoder = new google.maps.Geocoder();
  }

  private setFirstLocation(): void {
    if (navigator?.geolocation) {
      this.requestLocationPermission();
    } else {
      Logger.error('MapComponent', 'Geolocation is not supported by this browser.');
      this.setDefaultLocation(); // Fallback to default location if geolocation is not available
    }
  }

  // Request the user's location
  private requestLocationPermission(): void {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.handleGeolocationSuccess(position);
      },
      (error) => {
        this.handleGeolocationError(error);
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  }

  // Success handler for geolocation
  private handleGeolocationSuccess(position: GeolocationPosition): void {
    if (position?.coords?.latitude && position?.coords?.longitude) {
      this.latitude.set(position.coords.latitude);
      this.longitude.set(position.coords.longitude);
      this.selectedLocation.set({ lat: this.latitude(), lng: this.longitude() });
      this.getLocationInfo(this.latitude(), this.longitude()); // Get detailed address info
    } else {
      this.setDefaultLocation();
    }
    this.initMap();
  }

  // Error handler for geolocation
  private handleGeolocationError(error: GeolocationPositionError): void {
    Logger.error('MapComponent', 'Error getting current location', error);
    this.setDefaultLocation(); // Fallback to default location
    this.initMap();
  }

  private setDefaultLocation(): void {
    this.selectedLocation.set({ lat: 24.7136, lng: 46.6753 }); // Default to Riyadh
    this.latitude.set(this.selectedLocation().lat);
    this.longitude.set(this.selectedLocation().lng);
  }

  // Method to get location information (address, city, region, etc.)
  private getLocationInfo(lat: number, lng: number): void {
    const latLng = new google.maps.LatLng(lat, lng);
    this.geoCoder.geocode({ 'location': latLng }, (results: any[], status: any) => {
      if (status === google.maps.GeocoderStatus.OK) {
        if (results[0]) {
          // Initialize the variables
          let fullAddress: string = results[0]?.formatted_address || '';
          // Now that we have the address components, log or use them
          Logger.debug('Full Address:', fullAddress);

          // You can also set these values into the component's properties if you want to display them in the UI.
        } else {
          Logger.debug('No results found');
        }
      } else {
        Logger.debug('Geocoder failed due to:', status);
      }
    });
  }

  private initMap(): void {
    const mapOptions: any = {
      center: new google.maps.LatLng(this.selectedLocation().lat, this.selectedLocation().lng),
      zoom: this.zoom(),
      mapTypeId: google.maps.MapTypeId.ROADMAP,
    };

    this.map = new google.maps.Map(this.mapContainer.nativeElement, mapOptions);

    // Create a static marker fixed at the center
    this.marker = new google.maps.Marker({
      position: new google.maps.LatLng(this.selectedLocation().lat, this.selectedLocation().lng),
      map: this.map,
      draggable: false, // Marker should remain static at the center
      icon: {
        url: '', // You can change this to your custom icon
        size: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 32)
      }
    });

    // Emit the initial location right after initializing the map
    const initialLocation: Location = {
      lat: this.selectedLocation().lat,
      lng: this.selectedLocation().lng,
    };

    const initialLocationModel = LocationModel.create({
      latitude: initialLocation.lat,
      longitude: initialLocation.lng,
    });
    this.emitLocation.emit(initialLocationModel); // Emit the initial LocationModel

    Logger.debug('Initial Location emitted:', initialLocation);

    // Set up a listener for map center change
    google.maps.event.addListener(this.map, 'center_changed', () => {
      this.updateLocationFromMapCenter();
    });

    // Set up a listener for marker position change (if ever needed)
    google.maps.event.addListener(this.marker, 'position_changed', () => {
      this.updateLocationFromMapCenter();
    });
  }

  // Method to handle the input submission and update map center
  onSubmitLocation(): void {
    if (this.latitudeInput && this.longitudeInput) {
      const newLocation: Location = {
        lat: this.latitudeInput,
        lng: this.longitudeInput,
      };

      // Set map center and marker position
      this.map.setCenter(new google.maps.LatLng(newLocation.lat, newLocation.lng));
      this.marker.setPosition(new google.maps.LatLng(newLocation.lat, newLocation.lng));

      // Emit the updated location
      const locationModel = LocationModel.create({
        latitude: newLocation.lat,
        longitude: newLocation.lng,
      });
      this.emitLocation.emit(locationModel);

      // Reset input fields after submit
      this.latitudeInput = null;
      this.longitudeInput = null;

      Logger.debug('Submitted Location:', newLocation);
    } else {
      Logger.error('MapComponent', 'Please enter valid latitude and longitude');
      alert('Please enter valid latitude and longitude');
    }
  }

  // Input validation methods
  isLatitudeValid(): boolean {
    return this.latitudeInput !== null && this.latitudeInput >= -90 && this.latitudeInput <= 90;
  }

  isLongitudeValid(): boolean {
    return this.longitudeInput !== null && this.longitudeInput >= -180 && this.longitudeInput <= 180;
  }

  // Check if form is valid (both latitude and longitude are valid)
  isFormValid(): boolean {
    return this.isLatitudeValid() && this.isLongitudeValid();
  }

  // Method to handle input change events for validation
  onInputChange(): void {
    // Optional: You can add custom logic on each input change if needed
  }

  // Update the location from the map center
  private updateLocationFromMapCenter(): void {
    const center = this.map.getCenter();
    const newLocation: Location = {
      lat: center.lat(),
      lng: center.lng(),
    };

    this.selectedLocation.set(newLocation);

    // Log the location data before emitting it
    Logger.debug('Location before emit:', newLocation);

    // Emit the updated LocationModel
    const locationModel = LocationModel.create({
      latitude: newLocation.lat,
      longitude: newLocation.lng,
    });
    this.emitLocation.emit(locationModel); // Emit the updated LocationModel
    Logger.debug('Updated Location:', newLocation);
  }

  ngOnDestroy() {
    clearTimeout(this.markerMoveTimeout); // Clear any timeouts when the component is destroyed
  }
}
